import { useEffect, useMemo, useRef, useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Link } from '@router'
import { OnlineFieldset } from '@components/OnlineFieldset.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import type { MealPlanDay, Recipe, RecipeId } from '@hewey/shared'
import { dayLabel } from './dates.ts'
import styles from './DayRow.module.css'

type Props = {
	date: Date
	day: MealPlanDay | null
	editable: boolean
	isToday: boolean
	recipes: Recipe[]
	onSave: (
		title: string | null,
		note: string | null,
		recipeId: RecipeId | null,
	) => void
}

// Trim + null-empty so empty strings round-trip as `null` to the server.
const norm = (s: string): string | null => {
	const t = s.trim()
	return t.length === 0 ? null : t
}

const MAX_SUGGESTIONS = 5

export const DayRow = ({
	date,
	day,
	editable,
	isToday,
	recipes,
	onSave,
}: Props) => {
	const { weekday, date: dateStr } = dayLabel(date)

	const online = useOnline()
	const [title, setTitle] = useState(day?.title ?? '')
	const [note, setNote] = useState(day?.note ?? '')
	const [recipeId, setRecipeId] = useState<RecipeId | null>(
		day?.recipeId ?? null,
	)
	const [focused, setFocused] = useState(false)
	// True while either field is focused — a background poll bringing a remote
	// change must not resync (and clobber) an edit in progress.
	const editingRef = useRef(false)

	// Track the saved values so blur only fires a PUT when something changed.
	const savedTitle = useRef(day?.title ?? null)
	const savedNote = useRef(day?.note ?? null)
	const savedRecipeId = useRef<RecipeId | null>(day?.recipeId ?? null)

	// Sync from props when the persisted day changes externally (poll,
	// other-device update). Skip while editing so we don't overwrite what the
	// user is typing; the ref-comparison in `commit` keeps a blur-triggered
	// save from clobbering itself.
	useEffect(() => {
		if (editingRef.current) return
		setTitle(day?.title ?? '')
		setNote(day?.note ?? '')
		setRecipeId(day?.recipeId ?? null)
		savedTitle.current = day?.title ?? null
		savedNote.current = day?.note ?? null
		savedRecipeId.current = day?.recipeId ?? null
	}, [day?.title, day?.note, day?.recipeId])

	const linkedRecipe = useMemo(
		() =>
			recipeId ? (recipes.find((r) => r.id === recipeId) ?? null) : null,
		[recipeId, recipes],
	)

	const suggestions = useMemo(() => {
		const q = title.trim().toLowerCase()
		// 1- or 2-char queries match too many recipes to be useful and pop
		// the dropdown on every first keystroke. Wait for a real prefix.
		if (q.length < 3) return []
		// startsWith first, then substring, then dedupe.
		const starts: Recipe[] = []
		const contains: Recipe[] = []
		for (const r of recipes) {
			const t = r.title.toLowerCase()
			if (t.startsWith(q)) starts.push(r)
			else if (t.includes(q)) contains.push(r)
		}
		return [...starts, ...contains].slice(0, MAX_SUGGESTIONS)
	}, [title, recipes])

	const writeBack = (
		nextTitle: string | null,
		nextNote: string | null,
		nextRecipeId: RecipeId | null,
	) => {
		if (
			nextTitle === savedTitle.current &&
			nextNote === savedNote.current &&
			nextRecipeId === savedRecipeId.current
		) {
			return
		}
		savedTitle.current = nextTitle
		savedNote.current = nextNote
		savedRecipeId.current = nextRecipeId
		onSave(nextTitle, nextNote, nextRecipeId)
	}

	const commit = () => {
		const nextTitle = norm(title)
		const nextNote = norm(note)
		// Break the recipe link if the title no longer matches the linked
		// recipe — editing the title is the natural unlink gesture. Also
		// clears silently if the linked recipe was deleted (find returns null).
		const nextRecipeId =
			linkedRecipe && nextTitle === linkedRecipe.title ? recipeId : null
		if (nextRecipeId !== recipeId) setRecipeId(nextRecipeId)
		writeBack(nextTitle, nextNote, nextRecipeId)
	}

	const pickRecipe = (r: Recipe) => {
		setTitle(r.title)
		setRecipeId(r.id)
		setFocused(false)
		writeBack(r.title, norm(note), r.id)
	}

	const showSuggestions = focused && suggestions.length > 0

	const rowClass = [
		styles.row,
		isToday ? styles.today : '',
		editable ? '' : styles.readonly,
		linkedRecipe ? styles.hasRecipe : '',
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={rowClass}>
			<div className={styles.label}>
				<span className={styles.weekday}>{weekday}</span>
				<span className={styles.date}>{dateStr}</span>
			</div>
			{editable ? (
				<OnlineFieldset className={styles.body}>
					<div className={styles.titleField}>
						<input
							className={styles.title}
							value={title}
							placeholder={online ? 'Add a meal' : undefined}
							maxLength={200}
							onChange={(e) => setTitle(e.target.value)}
							onFocus={() => {
								editingRef.current = true
								setFocused(true)
							}}
							onBlur={() => {
								editingRef.current = false
								setFocused(false)
								commit()
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault()
									;(e.target as HTMLInputElement).blur()
								} else if (e.key === 'Escape') {
									setFocused(false)
								}
							}}
						/>
						{showSuggestions ? (
							<ul className={styles.suggestions} role="listbox">
								{suggestions.map((r) => (
									<li key={r.id}>
										<button
											type="button"
											className={styles.suggestion}
											// mouseDown fires before blur — preventDefault
											// keeps focus so we can update state synchronously
											// without the input's blur clobbering us.
											onMouseDown={(e) => {
												e.preventDefault()
												pickRecipe(r)
											}}
										>
											<BookOpen
												size={14}
												className={
													styles.suggestionIcon
												}
												aria-hidden
											/>
											<span
												className={
													styles.suggestionTitle
												}
											>
												{r.title}
											</span>
										</button>
									</li>
								))}
							</ul>
						) : null}
					</div>
					<input
						className={styles.note}
						value={note}
						placeholder={online ? 'Add a note' : undefined}
						maxLength={200}
						onChange={(e) => setNote(e.target.value)}
						onFocus={() => {
							editingRef.current = true
						}}
						onBlur={() => {
							editingRef.current = false
							commit()
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault()
								;(e.target as HTMLInputElement).blur()
							}
						}}
					/>
				</OnlineFieldset>
			) : (
				<div className={styles.body}>
					<div className={styles.title}>
						{day?.title || (
							<span className={styles.placeholder}>—</span>
						)}
					</div>
					{day?.note ? (
						<div className={styles.note}>{day.note}</div>
					) : null}
				</div>
			)}
			{linkedRecipe ? (
				<Link
					className={styles.gotoRecipe}
					href={`/recipes/${linkedRecipe.id}`}
					aria-label={`Open recipe ${linkedRecipe.title}`}
					// In an editable row, clicking blurs the input first so the
					// pending edit commits, then navigates in one client-side go.
				>
					<BookOpen size={20} aria-hidden />
				</Link>
			) : null}
		</div>
	)
}
